type LocationLike = {
  state?: string | null;
  city?: string | null;
  county?: string | null;
};

const normalize = (value: string | null | undefined): string => (value ?? '').trim().toLowerCase();

const NYC_CITIES = new Set(['new york', 'new york city', 'manhattan', 'bronx', 'brooklyn', 'queens', 'staten island']);
const MICHIGAN_CITIES = new Set(['detroit', 'ann arbor']);
const CALIFORNIA_CITIES = new Set(['oakland', 'berkeley', 'san francisco', 'richmond']);

/**
 * Returns true when the property is in a jurisdiction that requires the
 * landlord to confirm a non-criminal pre-qualification decision before
 * viewing the criminal report (per the V3 ShareAble for Rentals integration
 * guide, page 7).
 *
 * Jurisdictions: NYC, NJ (state-wide), DC, Maryland (state-wide), Detroit/Ann
 * Arbor MI, Cook County IL, Oakland/Berkeley/San Francisco/Richmond CA.
 *
 * For the county-level rule (Cook IL), if the county is unknown, this returns
 * true conservatively: the cost of a false positive is one extra confirmation
 * click; the cost of a false negative is a compliance violation.
 */
export function isBifurcatedScreeningRequired(location: LocationLike | null | undefined): boolean {
  if (!location?.state) return false;

  const state = normalize(location.state);
  const city = normalize(location.city);
  const county = normalize(location.county);

  if (state === 'nj' || state === 'new jersey') return true;
  if (state === 'dc' || state === 'district of columbia') return true;
  if (state === 'md' || state === 'maryland') return true;

  if ((state === 'ny' || state === 'new york') && NYC_CITIES.has(city)) return true;
  if ((state === 'mi' || state === 'michigan') && MICHIGAN_CITIES.has(city)) return true;
  if ((state === 'ca' || state === 'california') && CALIFORNIA_CITIES.has(city)) return true;

  if (state === 'il' || state === 'illinois') {
    if (!county) return true;
    return county.includes('cook');
  }

  return false;
}
